// src/NewsDetail.js - Page détail d'une actualité
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './styles/NewsDetail.css';

const NewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadNewsDetail();
  }, [id]);

  const loadNewsDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/news/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setNews(response.data.data);
    } catch (err) {
      console.error('Erreur lors du chargement de l\'actualité:', err);
      setError('Impossible de charger l\'actualité.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderContent = (content) => {
    if (!content) return null;

    // Si c'est un JSON stringifié
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        if (Array.isArray(parsed)) {
          return parsed.map((block, index) => {
            if (block.contentFr) {
              return (
                <div 
                  key={index} 
                  className="content-block"
                  dangerouslySetInnerHTML={{ __html: block.contentFr }}
                />
              );
            }
            return null;
          });
        }
      } catch (e) {
        // Si ce n'est pas du JSON, afficher tel quel
        return <div dangerouslySetInnerHTML={{ __html: content }} />;
      }
    }

    // Si c'est déjà un objet
    if (Array.isArray(content)) {
      return content.map((block, index) => {
        if (block.contentFr) {
          return (
            <div 
              key={index} 
              className="content-block"
              dangerouslySetInnerHTML={{ __html: block.contentFr }}
            />
          );
        }
        return null;
      });
    }

    return <div dangerouslySetInnerHTML={{ __html: String(content) }} />;
  };

  if (loading) {
    return (
      <div style={{ padding: '30px' }}>
        <div className="news-loading">
          Chargement de l'actualité...
        </div>
      </div>
    );
  }

  if (error || !news) {
    return (
      <div style={{ padding: '30px' }}>
        <div className="news-error">
          ❌ {error || 'Actualité non trouvée'}
        </div>
        <button
          onClick={() => navigate('/news')}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            background: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          ← Retour aux actualités
        </button>
      </div>
    );
  }

  const imageUrl = news.image_une 
    ? `https://cdn.achat-or-et-argent.fr/ressources_v4/images/news/${news.image_une}`
    : null;

  // Générer l'URL SEO pour le site public
  const publicUrl = news.label_url_fr 
    ? `https://www.achat-or-et-argent.fr/cours-de-l-or-et-argent-${news.id}-${news.label_url_fr}`
    : `https://www.achat-or-et-argent.fr/actualites/${news.id}`;

  return (
    <div className="news-detail">
      <div className="news-detail-header">
        <button
          onClick={() => navigate('/news')}
          className="news-detail-back"
        >
          ← Retour aux actualités
        </button>
      </div>

      <article className="news-detail-article">
        {imageUrl && (
          <div className="news-detail-image">
            <img src={imageUrl} alt={news.label || news.titre_fr} onError={(e) => {
              e.target.style.display = 'none';
            }} />
          </div>
        )}

        <div className="news-detail-meta">
          <span className="news-detail-date">
            📅 {formatDate(news.dateposted)}
          </span>
          <span className="news-detail-id">
            ID: {news.id}
          </span>
          <span className={`news-detail-status ${news.status === 1 ? 'published' : 'draft'}`}>
            {news.status === 1 ? '✅ Publié' : '📝 Brouillon'}
          </span>
        </div>

        <h1 className="news-detail-title">{news.label || news.titre_fr}</h1>

        {/* SEO Info */}
        {news.label_url_fr && (
          <div style={{
            padding: '15px 40px',
            background: '#f0f8ff',
            border: '1px solid #d0e8ff',
            borderRadius: '8px',
            margin: '0 40px 20px 40px',
            fontSize: '0.9rem'
          }}>
            <strong>🔗 URL SEO :</strong>{' '}
            <code style={{ 
              background: '#e8f4ff', 
              padding: '4px 8px', 
              borderRadius: '4px',
              fontSize: '0.85rem'
            }}>
              {news.label_url_fr}
            </code>
            <br />
            <strong>🌐 URL publique :</strong>{' '}
            <a 
              href={publicUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#1976d2', textDecoration: 'underline' }}
            >
              {publicUrl}
            </a>
          </div>
        )}

        {news.resume_fr && (
          <div className="news-detail-resume">
            {news.resume_fr}
          </div>
        )}

        <div className="news-detail-content">
          {renderContent(news.content)}
        </div>

        <div className="news-detail-footer">
          <a 
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="news-detail-link"
          >
            🌐 Voir sur le site public →
          </a>
          
          {/* Liens multilingues si disponibles */}
          <div style={{ marginTop: '15px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {news.label_url_us && (
              <a 
                href={`https://www.achat-or-et-argent.fr/en/${news.label_url_us}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '8px 12px',
                  background: '#e3f2fd',
                  color: '#1976d2',
                  textDecoration: 'none',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  fontWeight: '600'
                }}
              >
                🇺🇸 English
              </a>
            )}
            {news.label_url_de && (
              <a 
                href={`https://www.achat-or-et-argent.fr/de/${news.label_url_de}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '8px 12px',
                  background: '#fff3e0',
                  color: '#e65100',
                  textDecoration: 'none',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  fontWeight: '600'
                }}
              >
                🇩🇪 Deutsch
              </a>
            )}
            {news.label_url_it && (
              <a 
                href={`https://www.achat-or-et-argent.fr/it/${news.label_url_it}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '8px 12px',
                  background: '#e8f5e9',
                  color: '#2e7d32',
                  textDecoration: 'none',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  fontWeight: '600'
                }}
              >
                🇮🇹 Italiano
              </a>
            )}
          </div>
        </div>
      </article>
    </div>
  );
};

export default NewsDetail;

